import { useState, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Stack,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  alpha,
  Button,
} from '@mui/material';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PeopleIcon from '@mui/icons-material/People';
import RepeatIcon from '@mui/icons-material/Repeat';
import axiosInstance from '../../lib/axios';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import {
  PaymentStatusChip,
  FulfillmentStatusChip,
} from '../../components/orders';
import {
  Order,
  OrderPaymentStatus,
  OrderFulfillmentStatus,
} from '../../types';

// Type for the pending payments API response
interface PendingPaymentGroup {
  id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  orderCount: number;
  totalPending: number;
  totalAmount: number;
  totalPaid: number;
  totalRemaining: number;
  orders: Order[];
}

interface PendingPaymentsResponse {
  groups: PendingPaymentGroup[];
  summary: {
    totalGroups: number;
    totalOrders: number;
    totalPendingAmount: number;
    totalAmount: number;
    totalPaid: number;
  };
}

// Types for accounts payable
interface RestockOrder {
  id: string;
  orderNumber: string;
  status: string;
  orderedAt: string;
  totalAmount: number;
  deliveredAt: string | null;
}

interface SupplierGroup {
  supplierId: string;
  supplierName: string;
  orderCount: number;
  totalAmount: number;
  restocks: RestockOrder[];
}

interface SalaryItem {
  staffId: string;
  staffName: string;
  baseSalary: number;
  paymentFrequency: string;
  currentPeriodGenerated: boolean;
  dueAmount: number;
}

interface RecurringExpenseItem {
  id: string;
  category: string;
  description: string;
  amount: number;
  recurringFrequency: string;
  lastRecordedDate: string | null;
}

interface AccountsPayableResponse {
  supplierRestocks: {
    groups: SupplierGroup[];
    totalAmount: number;
    totalOrders: number;
  };
  dueSalaries: {
    items: SalaryItem[];
    totalAmount: number;
    totalStaff: number;
  };
  recurringExpenses: {
    items: RecurringExpenseItem[];
    totalAmount: number;
    totalItems: number;
  };
  summary: {
    totalPayable: number;
    supplierTotal: number;
    salaryTotal: number;
    recurringTotal: number;
  };
}

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`payment-tabpanel-${index}`}
      aria-labelledby={`payment-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// Fetch pending payments from API
const fetchPendingPayments = async (groupBy: 'customer' | 'reseller'): Promise<PendingPaymentsResponse> => {
  const response = await axiosInstance.get('/orders/pending-payments', {
    params: { groupBy },
  });
  return response.data;
};

// Fetch accounts payable from API
const fetchAccountsPayable = async (): Promise<AccountsPayableResponse> => {
  const response = await axiosInstance.get('/expenses/accounts-payable');
  return response.data;
};

// Group card component
interface GroupCardProps {
  group: PendingPaymentGroup;
  isB2B: boolean;
  colors: Record<string, string>;
  onViewOrder: (orderId: string) => void;
}

const GroupCard = ({ group, isB2B, colors, onViewOrder }: GroupCardProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const animations = useAnimationStyles();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-DZ', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Card
      sx={{
        backgroundColor: colors.surface,
        border: `2px solid ${colors.border}`,
        borderRadius: theme.radius.sm,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 24px ${alpha(palette.warning, 0.15)}`,
        },
        ...animations.fadeIn,
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Group Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2.5,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: alpha(colors.border, 0.3),
            },
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: theme.radius.md,
                bgcolor: alpha(isB2B ? palette.accent : palette.info, 0.15),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isB2B ? (
                <BusinessIcon sx={{ fontSize: 24, color: palette.accent }} />
              ) : (
                <PersonIcon sx={{ fontSize: 24, color: palette.info }} />
              )}
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                  fontSize: typography.fontSize.md,
                }}
              >
                {group.name}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {group.email && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.textMuted,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    {group.email}
                  </Typography>
                )}
                {group.phone && group.email && (
                  <Typography sx={{ color: colors.textMuted }}>|</Typography>
                )}
                {group.phone && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.textMuted,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    {group.phone}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="caption"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.wider,
                  textTransform: 'uppercase',
                  mb: 0.5,
                  display: 'block',
                }}
              >
                {group.orderCount} {group.orderCount === 1 ? t('pendingPayments.group.order') : t('pendingPayments.group.orders')}
              </Typography>
              <Stack spacing={0.25}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    {t('pendingPayments.group.totalAmount')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.bold,
                      color: colors.textPrimary,
                    }}
                  >
                    {formatCurrency(group.totalAmount)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    {t('pendingPayments.group.paid')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.bold,
                      color: group.totalPaid > 0 ? palette.success : colors.textSecondary,
                    }}
                  >
                    {formatCurrency(group.totalPaid)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    {t('pendingPayments.group.remaining')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.extrabold,
                      color: group.totalRemaining > 0 ? palette.warning : palette.success,
                    }}
                  >
                    {formatCurrency(group.totalRemaining)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Expanded Orders */}
        <Collapse in={expanded}>
          <Box
            sx={{
              borderTop: `1px solid ${colors.border}`,
              bgcolor: alpha(colors.surfaceElevated, 0.5),
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: typography.fontWeight.bold,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wider,
                      color: colors.textMuted,
                      borderColor: colors.border,
                    }}
                  >
                    {t('pendingPayments.table.order')}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: typography.fontWeight.bold,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wider,
                      color: colors.textMuted,
                      borderColor: colors.border,
                    }}
                  >
                    {t('pendingPayments.table.date')}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: typography.fontWeight.bold,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wider,
                      color: colors.textMuted,
                      borderColor: colors.border,
                    }}
                  >
                    {t('pendingPayments.table.payment')}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: typography.fontWeight.bold,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wider,
                      color: colors.textMuted,
                      borderColor: colors.border,
                    }}
                  >
                    {t('pendingPayments.table.fulfillment')}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: typography.fontWeight.bold,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wider,
                      color: colors.textMuted,
                      borderColor: colors.border,
                    }}
                  >
                    {t('pendingPayments.table.total')}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: typography.fontWeight.bold,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wider,
                      color: colors.textMuted,
                      borderColor: colors.border,
                    }}
                  >
                    {t('pendingPayments.table.paid')}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: typography.fontWeight.bold,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wider,
                      color: colors.textMuted,
                      borderColor: colors.border,
                    }}
                  >
                    {t('pendingPayments.table.remaining')}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderColor: colors.border,
                    }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {group.orders.map((order) => (
                  <TableRow
                    key={order.id}
                    sx={{
                      '&:hover': {
                        bgcolor: alpha(colors.border, 0.3),
                      },
                    }}
                  >
                    <TableCell sx={{ borderColor: colors.border }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon sx={{ fontSize: 16, color: palette.primary }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            fontWeight: typography.fontWeight.bold,
                            color: colors.textPrimary,
                            fontSize: typography.fontSize.sm,
                          }}
                        >
                          {order.trackingNumber || order.id?.substring(0, 8).toUpperCase()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.border }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: typography.fontFamily.display,
                          color: colors.textSecondary,
                          fontSize: typography.fontSize.sm,
                        }}
                      >
                        {formatDate(order.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.border }}>
                      <PaymentStatusChip status={order.paymentStatus as OrderPaymentStatus} size="small" />
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.border }}>
                      <FulfillmentStatusChip status={order.fulfillmentStatus as OrderFulfillmentStatus} size="small" />
                    </TableCell>
                    <TableCell align="right" sx={{ borderColor: colors.border }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontWeight: typography.fontWeight.bold,
                          color: colors.textPrimary,
                          fontSize: typography.fontSize.sm,
                        }}
                      >
                        {formatCurrency(order.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ borderColor: colors.border }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontWeight: typography.fontWeight.bold,
                          color: (order.paidAmount || 0) > 0 ? palette.success : colors.textSecondary,
                          fontSize: typography.fontSize.sm,
                        }}
                      >
                        {formatCurrency(order.paidAmount || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ borderColor: colors.border }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontWeight: typography.fontWeight.bold,
                          color: Math.max((order.totalAmount || 0) - (order.paidAmount || 0), 0) > 0 ? palette.warning : palette.success,
                          fontSize: typography.fontSize.sm,
                        }}
                      >
                        {formatCurrency(Math.max((order.totalAmount || 0) - (order.paidAmount || 0), 0))}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.border }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewOrder(order.id);
                        }}
                        sx={{
                          color: palette.primary,
                          '&:hover': {
                            bgcolor: alpha(palette.primary, 0.1),
                          },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// Supplier Group Card component
interface SupplierGroupCardProps {
  group: SupplierGroup;
  colors: Record<string, string>;
}

const SupplierGroupCard = ({ group, colors }: SupplierGroupCardProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const animations = useAnimationStyles();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-DZ', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Card
      sx={{
        backgroundColor: colors.surface,
        border: `2px solid ${colors.border}`,
        borderRadius: theme.radius.sm,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 24px ${alpha(palette.error, 0.15)}`,
        },
        ...animations.fadeIn,
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Group Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2.5,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: alpha(colors.border, 0.3),
            },
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: theme.radius.md,
                bgcolor: alpha(palette.error, 0.15),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StorefrontIcon sx={{ fontSize: 24, color: palette.error }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                  fontSize: typography.fontSize.md,
                }}
              >
                {group.supplierName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.textMuted,
                  fontSize: typography.fontSize.xs,
                }}
              >
                {group.orderCount} {group.orderCount === 1 ? t('accountsPayable.supplier.order') : t('accountsPayable.supplier.orders')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="caption"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.wider,
                  textTransform: 'uppercase',
                  mb: 0.5,
                  display: 'block',
                }}
              >
                {t('accountsPayable.supplier.totalOwed')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: palette.error,
                }}
              >
                {formatCurrency(group.totalAmount)}
              </Typography>
            </Box>
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Expanded Restock Orders */}
        <Collapse in={expanded}>
          <Box
            sx={{
              borderTop: `1px solid ${colors.border}`,
              bgcolor: alpha(colors.surfaceElevated, 0.5),
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: typography.fontWeight.bold,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wider,
                      color: colors.textMuted,
                      borderColor: colors.border,
                    }}
                  >
                    {t('accountsPayable.supplier.table.orderNumber')}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: typography.fontWeight.bold,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wider,
                      color: colors.textMuted,
                      borderColor: colors.border,
                    }}
                  >
                    {t('accountsPayable.supplier.table.status')}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: typography.fontWeight.bold,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wider,
                      color: colors.textMuted,
                      borderColor: colors.border,
                    }}
                  >
                    {t('accountsPayable.supplier.table.orderedDate')}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: typography.fontWeight.bold,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wider,
                      color: colors.textMuted,
                      borderColor: colors.border,
                    }}
                  >
                    {t('accountsPayable.supplier.table.amount')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.restocks.map((restock) => (
                  <TableRow
                    key={restock.id}
                    sx={{
                      '&:hover': {
                        bgcolor: alpha(colors.border, 0.3),
                      },
                    }}
                  >
                    <TableCell sx={{ borderColor: colors.border }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon sx={{ fontSize: 16, color: palette.error }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            fontWeight: typography.fontWeight.bold,
                            color: colors.textPrimary,
                            fontSize: typography.fontSize.sm,
                          }}
                        >
                          {restock.orderNumber}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.border }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: typography.fontFamily.display,
                          color: colors.textSecondary,
                          fontSize: typography.fontSize.sm,
                        }}
                      >
                        {restock.status}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.border }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: typography.fontFamily.display,
                          color: colors.textSecondary,
                          fontSize: typography.fontSize.sm,
                        }}
                      >
                        {formatDate(restock.orderedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ borderColor: colors.border }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontWeight: typography.fontWeight.bold,
                          color: palette.error,
                          fontSize: typography.fontSize.sm,
                        }}
                      >
                        {formatCurrency(restock.totalAmount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export const PendingPayments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  // Top-level tab state: 0 = Receivable, 1 = Payable
  const [mainTabValue, setMainTabValue] = useState(0);

  // Sub-tab state for Receivable: 0 = B2C (customer), 1 = B2B (reseller)
  const [receivableTabValue, setReceivableTabValue] = useState(0);
  const groupBy = receivableTabValue === 0 ? 'customer' : 'reseller';

  // Theme-aware colors
  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  // Fetch pending payments (receivable)
  const { data: receivableData, isLoading: receivableLoading, error: receivableError } = useQuery({
    queryKey: ['pendingPayments', groupBy],
    queryFn: () => fetchPendingPayments(groupBy),
    enabled: mainTabValue === 0,
  });

  // Fetch accounts payable
  const { data: payableData, isLoading: payableLoading, error: payableError } = useQuery({
    queryKey: ['accountsPayable'],
    queryFn: fetchAccountsPayable,
    enabled: mainTabValue === 1,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const handleViewOrder = (orderId: string) => {
    navigate(`/app/orders/show/${orderId}`);
  };

  const handleMainTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setMainTabValue(newValue);
  };

  const handleReceivableTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setReceivableTabValue(newValue);
  };

  // Summary stats for receivable
  const receivableSummary = useMemo(() => {
    if (!receivableData?.summary) {
      return {
        totalGroups: 0,
        totalOrders: 0,
        totalPendingAmount: 0,
        totalAmount: 0,
        totalPaid: 0,
      };
    }
    return receivableData.summary;
  }, [receivableData]);

  // Summary stats for payable
  const payableSummary = useMemo(() => {
    if (!payableData?.summary) {
      return {
        totalPayable: 0,
        supplierTotal: 0,
        salaryTotal: 0,
        recurringTotal: 0,
      };
    }
    return payableData.summary;
  }, [payableData]);

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Industrial Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <MoneyOffIcon sx={{ color: palette.warning, fontSize: 24 }} />
              <Typography
                variant="overline"
                sx={{
                  color: palette.warning,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                {t('pendingPayments.header.managementLabel')}
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              {t('pendingPayments.header.title')}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/app/orders')}
            sx={{
              borderColor: colors.border,
              color: colors.textSecondary,
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.sm,
              px: 3,
              py: 1.5,
              borderRadius: theme.radius.sm,
              '&:hover': {
                borderColor: palette.primary,
                bgcolor: alpha(palette.primary, 0.05),
              },
            }}
          >
            {t('pendingPayments.header.viewAllOrders')}
          </Button>
        </Box>
      </Box>

      {/* Top-Level Tabs: Receivable vs Payable */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          mb: 3,
          ...animations.slideUp(0.05),
        }}
      >
        <Tabs
          value={mainTabValue}
          onChange={handleMainTabChange}
          sx={{
            borderBottom: `1px solid ${colors.border}`,
            '& .MuiTab-root': {
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.bold,
              fontSize: typography.fontSize.sm,
              textTransform: 'uppercase',
              letterSpacing: typography.letterSpacing.wider,
              minHeight: 56,
              color: colors.textSecondary,
              '&.Mui-selected': {
                color: palette.primary,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: palette.primary,
              height: 3,
            },
          }}
        >
          <Tab
            icon={<CallReceivedIcon />}
            iconPosition="start"
            label={t('pendingPayments.mainTabs.receivable')}
            id="payment-tab-0"
            aria-controls="payment-tabpanel-0"
          />
          <Tab
            icon={<CallMadeIcon />}
            iconPosition="start"
            label={t('pendingPayments.mainTabs.payable')}
            id="payment-tab-1"
            aria-controls="payment-tabpanel-1"
          />
        </Tabs>
      </Card>

      {/* Receivable TabPanel */}
      <TabPanel value={mainTabValue} index={0}>

        {/* Receivable Stats Dashboard */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3, ...animations.slideUp(0.1) }}>
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 2.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.warning,
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${alpha(palette.warning, 0.15)}`,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  {t('pendingPayments.stats.totalRemaining')}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: palette.warning,
                    lineHeight: 1,
                    mt: 1,
                    fontSize: typography.fontSize.lg,
                  }}
                >
                  {formatCurrency(receivableSummary.totalPendingAmount)}
                </Typography>
              </Box>
              <TrendingUpIcon sx={{ fontSize: 40, color: alpha(palette.warning, 0.2) }} />
            </Box>
          </Card>

          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 2.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.success,
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${alpha(palette.success, 0.15)}`,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  {t('pendingPayments.stats.totalPaid')}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: palette.success,
                    lineHeight: 1,
                    mt: 1,
                    fontSize: typography.fontSize.lg,
                  }}
                >
                  {formatCurrency(receivableSummary.totalPaid)}
                </Typography>
              </Box>
              <AccountBalanceWalletIcon sx={{ fontSize: 40, color: alpha(palette.success, 0.2) }} />
            </Box>
          </Card>

          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 2.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.info,
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${alpha(palette.info, 0.15)}`,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  {receivableTabValue === 0 ? t('pendingPayments.stats.customers') : t('pendingPayments.stats.businesses')}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: palette.info,
                    lineHeight: 1,
                    mt: 1,
                  }}
                >
                  {receivableSummary.totalGroups}
                </Typography>
              </Box>
              <GroupIcon sx={{ fontSize: 40, color: alpha(palette.info, 0.2) }} />
            </Box>
          </Card>

          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 2.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.primary,
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${alpha(palette.primary, 0.15)}`,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  {t('pendingPayments.stats.orders')}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: palette.primary,
                    lineHeight: 1,
                    mt: 1,
                  }}
                >
                  {receivableSummary.totalOrders}
                </Typography>
              </Box>
              <ShoppingCartIcon sx={{ fontSize: 40, color: alpha(palette.primary, 0.2) }} />
            </Box>
          </Card>
        </Box>

        {/* B2C/B2B Tabs */}
        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            mb: 3,
            ...animations.slideUp(0.15),
          }}
        >
          <Tabs
            value={receivableTabValue}
            onChange={handleReceivableTabChange}
            sx={{
              borderBottom: `1px solid ${colors.border}`,
              '& .MuiTab-root': {
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.bold,
                fontSize: typography.fontSize.sm,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.wider,
                minHeight: 56,
                color: colors.textSecondary,
                '&.Mui-selected': {
                  color: palette.primary,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: palette.primary,
                height: 3,
              },
            }}
          >
            <Tab
              icon={<PersonIcon />}
              iconPosition="start"
              label={t('pendingPayments.tabs.b2c')}
            />
            <Tab
              icon={<BusinessIcon />}
              iconPosition="start"
              label={t('pendingPayments.tabs.b2b')}
            />
          </Tabs>
        </Card>

        {/* Receivable Content */}
        {receivableLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Stack alignItems="center" gap={2}>
              <CircularProgress size={48} sx={{ color: palette.primary }} />
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                {t('pendingPayments.messages.loading')}
              </Typography>
            </Stack>
          </Box>
        ) : receivableError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {receivableError instanceof Error ? receivableError.message : t('pendingPayments.messages.error')}
          </Alert>
        ) : !receivableData?.groups || receivableData.groups.length === 0 ? (
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 6,
              textAlign: 'center',
              ...animations.slideUp(0.2),
            }}
          >
            <MoneyOffIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                mb: 1,
              }}
            >
              {t('pendingPayments.messages.noPayments')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.textMuted,
                mb: 3,
              }}
            >
              {receivableTabValue === 0
                ? t('pendingPayments.messages.customersUpToDate')
                : t('pendingPayments.messages.businessesUpToDate')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/app/orders')}
              sx={{
                backgroundColor: palette.primary,
                color: '#FFFFFF',
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.sm,
                px: 4,
                py: 1.5,
                borderRadius: theme.radius.sm,
                '&:hover': {
                  backgroundColor: alpha(palette.primary, 0.8),
                },
              }}
            >
              {t('pendingPayments.header.viewAllOrders')}
            </Button>
          </Card>
        ) : (
          <Stack spacing={2} sx={{ ...animations.slideUp(0.2) }}>
            {receivableData.groups.map((group, index) => (
              <Box key={group.id || `anonymous-${index}`} sx={animations.slideUp(0.2 + index * 0.05)}>
                <GroupCard
                  group={group}
                  isB2B={receivableTabValue === 1}
                  colors={colors}
                  onViewOrder={handleViewOrder}
                />
              </Box>
            ))}
          </Stack>
        )}
      </TabPanel>

      {/* Payable TabPanel */}
      <TabPanel value={mainTabValue} index={1}>
        {/* Payable Stats Dashboard */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3, ...animations.slideUp(0.1) }}>
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 2.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.error,
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${alpha(palette.error, 0.15)}`,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  {t('accountsPayable.stats.totalPayable')}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: palette.error,
                    lineHeight: 1,
                    mt: 1,
                    fontSize: typography.fontSize.lg,
                  }}
                >
                  {formatCurrency(payableSummary.totalPayable)}
                </Typography>
              </Box>
              <TrendingUpIcon sx={{ fontSize: 40, color: alpha(palette.error, 0.2) }} />
            </Box>
          </Card>

          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 2.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.warning,
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${alpha(palette.warning, 0.15)}`,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  {t('accountsPayable.stats.suppliers')}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: palette.warning,
                    lineHeight: 1,
                    mt: 1,
                    fontSize: typography.fontSize.lg,
                  }}
                >
                  {formatCurrency(payableSummary.supplierTotal)}
                </Typography>
              </Box>
              <StorefrontIcon sx={{ fontSize: 40, color: alpha(palette.warning, 0.2) }} />
            </Box>
          </Card>

          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 2.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.info,
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${alpha(palette.info, 0.15)}`,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  {t('accountsPayable.stats.salaries')}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: palette.info,
                    lineHeight: 1,
                    mt: 1,
                    fontSize: typography.fontSize.lg,
                  }}
                >
                  {formatCurrency(payableSummary.salaryTotal)}
                </Typography>
              </Box>
              <PeopleIcon sx={{ fontSize: 40, color: alpha(palette.info, 0.2) }} />
            </Box>
          </Card>

          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 2.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.accent,
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${alpha(palette.accent, 0.15)}`,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  {t('accountsPayable.stats.recurring')}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: palette.accent,
                    lineHeight: 1,
                    mt: 1,
                    fontSize: typography.fontSize.lg,
                  }}
                >
                  {formatCurrency(payableSummary.recurringTotal)}
                </Typography>
              </Box>
              <RepeatIcon sx={{ fontSize: 40, color: alpha(palette.accent, 0.2) }} />
            </Box>
          </Card>
        </Box>

        {/* Payable Content */}
        {payableLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Stack alignItems="center" gap={2}>
              <CircularProgress size={48} sx={{ color: palette.primary }} />
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                {t('accountsPayable.messages.loading')}
              </Typography>
            </Stack>
          </Box>
        ) : payableError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {payableError instanceof Error ? payableError.message : t('accountsPayable.messages.error')}
          </Alert>
        ) : !payableData || (
          payableData.supplierRestocks.groups.length === 0 &&
          payableData.dueSalaries.items.length === 0 &&
          payableData.recurringExpenses.items.length === 0
        ) ? (
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 6,
              textAlign: 'center',
              ...animations.slideUp(0.2),
            }}
          >
            <AccountBalanceWalletIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                mb: 1,
              }}
            >
              {t('accountsPayable.messages.noPayables')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.textMuted,
              }}
            >
              {t('accountsPayable.messages.allUpToDate')}
            </Typography>
          </Card>
        ) : (
          <Stack spacing={3}>
            {/* Supplier Restocks Section */}
            {payableData.supplierRestocks.groups.length > 0 && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textPrimary,
                    mb: 2,
                  }}
                >
                  {t('accountsPayable.sections.suppliers')}
                </Typography>
                <Stack spacing={2}>
                  {payableData.supplierRestocks.groups.map((group, index) => (
                    <Box key={group.supplierId} sx={animations.slideUp(0.2 + index * 0.05)}>
                      <SupplierGroupCard group={group} colors={colors} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Due Salaries Section */}
            {payableData.dueSalaries.items.length > 0 && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textPrimary,
                    mb: 2,
                  }}
                >
                  {t('accountsPayable.sections.salaries')}
                </Typography>
                <Card
                  sx={{
                    backgroundColor: colors.surface,
                    border: `2px solid ${colors.border}`,
                    borderRadius: theme.radius.sm,
                    overflow: 'hidden',
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: typography.fontWeight.bold,
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.xs,
                            textTransform: 'uppercase',
                            letterSpacing: typography.letterSpacing.wider,
                            color: colors.textMuted,
                            borderColor: colors.border,
                          }}
                        >
                          {t('accountsPayable.salary.table.staff')}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: typography.fontWeight.bold,
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.xs,
                            textTransform: 'uppercase',
                            letterSpacing: typography.letterSpacing.wider,
                            color: colors.textMuted,
                            borderColor: colors.border,
                          }}
                        >
                          {t('accountsPayable.salary.table.frequency')}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: typography.fontWeight.bold,
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.xs,
                            textTransform: 'uppercase',
                            letterSpacing: typography.letterSpacing.wider,
                            color: colors.textMuted,
                            borderColor: colors.border,
                          }}
                        >
                          {t('accountsPayable.salary.table.amount')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payableData.dueSalaries.items.map((salary) => (
                        <TableRow
                          key={salary.staffId}
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(colors.border, 0.3),
                            },
                          }}
                        >
                          <TableCell sx={{ borderColor: colors.border }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonIcon sx={{ fontSize: 16, color: palette.info }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: typography.fontFamily.display,
                                  fontWeight: typography.fontWeight.bold,
                                  color: colors.textPrimary,
                                  fontSize: typography.fontSize.sm,
                                }}
                              >
                                {salary.staffName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderColor: colors.border }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.display,
                                color: colors.textSecondary,
                                fontSize: typography.fontSize.sm,
                              }}
                            >
                              {salary.paymentFrequency}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ borderColor: colors.border }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.mono,
                                fontWeight: typography.fontWeight.bold,
                                color: palette.info,
                                fontSize: typography.fontSize.sm,
                              }}
                            >
                              {formatCurrency(salary.dueAmount)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </Box>
            )}

            {/* Recurring Expenses Section */}
            {payableData.recurringExpenses.items.length > 0 && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textPrimary,
                    mb: 2,
                  }}
                >
                  {t('accountsPayable.sections.recurring')}
                </Typography>
                <Card
                  sx={{
                    backgroundColor: colors.surface,
                    border: `2px solid ${colors.border}`,
                    borderRadius: theme.radius.sm,
                    overflow: 'hidden',
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: typography.fontWeight.bold,
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.xs,
                            textTransform: 'uppercase',
                            letterSpacing: typography.letterSpacing.wider,
                            color: colors.textMuted,
                            borderColor: colors.border,
                          }}
                        >
                          {t('accountsPayable.recurring.table.description')}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: typography.fontWeight.bold,
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.xs,
                            textTransform: 'uppercase',
                            letterSpacing: typography.letterSpacing.wider,
                            color: colors.textMuted,
                            borderColor: colors.border,
                          }}
                        >
                          {t('accountsPayable.recurring.table.frequency')}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: typography.fontWeight.bold,
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.xs,
                            textTransform: 'uppercase',
                            letterSpacing: typography.letterSpacing.wider,
                            color: colors.textMuted,
                            borderColor: colors.border,
                          }}
                        >
                          {t('accountsPayable.recurring.table.amount')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payableData.recurringExpenses.items.map((expense) => (
                        <TableRow
                          key={expense.id}
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(colors.border, 0.3),
                            },
                          }}
                        >
                          <TableCell sx={{ borderColor: colors.border }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <RepeatIcon sx={{ fontSize: 16, color: palette.accent }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: typography.fontFamily.display,
                                  fontWeight: typography.fontWeight.bold,
                                  color: colors.textPrimary,
                                  fontSize: typography.fontSize.sm,
                                }}
                              >
                                {expense.description}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderColor: colors.border }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.display,
                                color: colors.textSecondary,
                                fontSize: typography.fontSize.sm,
                              }}
                            >
                              {expense.recurringFrequency}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ borderColor: colors.border }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.mono,
                                fontWeight: typography.fontWeight.bold,
                                color: palette.accent,
                                fontSize: typography.fontSize.sm,
                              }}
                            >
                              {formatCurrency(expense.amount)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </Box>
            )}
          </Stack>
        )}
      </TabPanel>
    </Box>
  );
};

export default PendingPayments;
